import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  courtCasesApi,
  extractList,
  extractOne,
  userNotificationSettingsApi,
  authApi,
} from '../../api/facades';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
    const queryClient = useQueryClient();
    const userData = user || {
      name: 'User',
      email: 'user@example.com',
    };
    const roles = (userData as any)?.roles || [];
    const permissions = (userData as any)?.permissions || [];
    const hasManageSettings =
      Array.isArray(permissions) && permissions.some((p: any) => p?.slug === 'manage_settings');
    const isAdminRole =
      Array.isArray(roles) && roles.some((r: any) => String(r?.slug).toLowerCase() === 'admin');
    const canManageSettings =
      hasManageSettings ||
      isAdminRole ||
      (userData as any)?.user_type === 'Admin' ||
      (userData as any)?.is_admin === true ||
      (userData as any)?.role === 'Admin';
    const displayName =
      userData.name ||
      [(userData as any).people?.first_name, (userData as any).people?.last_name].filter(Boolean).join(' ') ||
      'User';
    const displayEmail = userData.email || 'user@example.com';
    const primaryRoleFromArray =
      Array.isArray(roles) && roles.length > 0
        ? roles[0]?.slug || roles[0]?.description || roles[0]?.name
        : null;
    const displayRole =
      (primaryRoleFromArray && String(primaryRoleFromArray)) ||
      (userData as any).role ||
      (userData as any).title ||
      'Attorney';
    // Recent court cases
    const { data: casesRes } = useQuery({
      queryKey: ['court-cases', 'profile'],
      queryFn: () => courtCasesApi.list({ perPage: 10 }),
    });
    const recentCases = extractList(casesRes).slice(0, 3);
    // Notification settings
    const {
      data: notifRes,
      isLoading: loadingNotif,
    } = useQuery({
      queryKey: ['user-notification-settings'],
      queryFn: () => userNotificationSettingsApi.get(),
    });
    const notifSettings = extractOne(notifRes) as { calendar_deadlines_enabled?: boolean } | null;
    const saveNotifMutation = useMutation({
      mutationFn: (enabled: boolean) =>
        userNotificationSettingsApi.save({ calendar_deadlines_enabled: enabled }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-notification-settings'] });
        toast.success('Notification settings updated.');
      },
      onError: (err: any) => {
        toast.error(err?.body?.message || err?.message || 'Failed to update notifications.');
      },
    });
    // Edit profile modal
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editName, setEditName] = useState(displayName);
    const openEditModal = () => {
      setEditName(displayName);
      setIsEditOpen(true);
    };
    // Change password (in modal)
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwLoading, setPwLoading] = useState(false);
    const handleChangePassword = async () => {
      if (newPw.length < 8) {
        toast.error('New password must be at least 8 characters.');
        return;
      }
      if (newPw !== confirmPw) {
        toast.error('New passwords do not match.');
        return;
      }
      setPwLoading(true);
      try {
        await authApi.changePassword({
          current_password: currentPw,
          new_password: newPw,
          new_password_confirmation: confirmPw,
        });
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
        toast.success('Password updated.');
      } catch (err: any) {
        const msg =
          err?.body?.errors?.current_password?.[0] ||
          err?.body?.message ||
          err?.message ||
          'Failed to update password.';
        toast.error(msg);
      } finally {
        setPwLoading(false);
      }
    };
    return (
      <div className="w-full">
        {/* Profile Header with Cover Image */}
        <div
          className="relative flex items-center p-0 mt-6 overflow-hidden bg-center bg-cover min-h-75 rounded-2xl"
          style={{ backgroundImage: "url('/parten.jpg')", backgroundPositionY: '50%' }}
        >
          <span className="absolute inset-y-0 w-full h-full bg-center bg-cover bg-gradient-to-tl from-emerald-800 to-emerald-600 opacity-40"></span>
        </div>
        {/* Profile Card */}
        <div className="relative flex flex-col flex-auto min-w-0 p-4 mx-6 -mt-16 overflow-hidden break-words border-0 rounded-2xl bg-white/80 dark:bg-stone-800/80
  bg-clip-border backdrop-blur-2xl backdrop-saturate-200">
          <div className="flex flex-wrap -mx-3">
            {/* Profile Image */}
            <div className="flex-none w-auto max-w-full px-3">
              <div className="text-base ease-soft-in-out h-18.5 w-18.5 relative inline-flex items-center justify-center rounded-xl text-white transition-all duration-200">       
                <div className="w-full h-full flex items-center justify-center bg-slate-200 dark:bg-slate-700 rounded-xl">
                  <svg
                    className="w-12 h-12 text-slate-500 dark:text-slate-300"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
            {/* Profile Name and Title */}
            <div className="flex-none w-auto max-w-full px-3 my-auto">
              <div className="h-full">
                <h5 className="mb-1 text-slate-700 dark:text-slate-100">{displayName}</h5>
                <p className="mb-0 font-semibold leading-normal text-sm text-slate-600 dark:text-slate-300">
                  {displayRole}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="w-full p-6 mx-auto">
          <div className="flex flex-wrap -mx-3">
            {/* System Settings / Notifications */}
            <div className="w-full max-w-full px-3 xl:w-4/12">
              <div className="relative flex flex-col h-full min-w-0 break-words bg-white dark:bg-stone-800 border-0 rounded-2xl bg-clip-border">
                <div className="p-4 pb-0 mb-0 bg-white dark:bg-stone-800 border-b-0 rounded-t-2xl">
                  <h6 className="mb-0 text-slate-700 dark:text-slate-100">System Settings</h6>
                </div>
                <div className="flex-auto p-4">
                  <h6 className="font-bold leading-tight uppercase text-xs text-slate-500 dark:text-slate-400">
                    Notifications
                  </h6>
                  <ul className="flex flex-col pl-0 mb-0 rounded-lg">
                    <li className="relative block px-0 py-2 bg-white dark:bg-stone-800 border-0 rounded-lg text-inherit">
                      <div className="min-h-6 mb-0.5 flex items-center justify-between pl-0">
                        <div className="flex-1 pr-4">
                          <p className="mb-0 text-sm text-slate-700 dark:text-slate-200">
                            Calendar & deadline reminders
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            Receive notifications for court dates, filing deadlines, and key case milestones.
                          </p>
                        </div>
                        <div className="flex items-center">
                          <button
                            type="button"
                            disabled={loadingNotif || saveNotifMutation.isPending}
                            onClick={() => {
                              const current = !!notifSettings?.calendar_deadlines_enabled;
                              saveNotifMutation.mutate(!current);
                            }}
                            className={`mt-0.54 rounded-10 duration-250 ease-soft-in-out after:rounded-circle after:duration-250 h-5 relative w-10 cursor-pointer
  appearance-none border border-solid ${
                              notifSettings?.calendar_deadlines_enabled
                                ? 'border-emerald-600 bg-emerald-600'
                                : 'border-gray-200 bg-emerald-100'
                            } bg-none bg-contain bg-left bg-no-repeat align-top transition-all`}
                            aria-pressed={!!notifSettings?.calendar_deadlines_enabled}
                          >
                            <span
                              className={`absolute top-px h-4 w-4 bg-white rounded-full transform transition-transform ${
                                notifSettings?.calendar_deadlines_enabled ? 'translate-x-5' : 'translate-x-px'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </li>
                  </ul>
                  
                </div>
              </div>
            </div>
            {/* Profile Information */}
            <div className="w-full max-w-full px-3 lg-max:mt-6 xl:w-4/12">
              <div className="relative flex flex-col h-full min-w-0 break-words bg-white dark:bg-stone-800 border-0 rounded-2xl bg-clip-border">
                <div className="p-4 pb-0 mb-0 bg-white dark:bg-stone-800 border-b-0 rounded-t-2xl">
                  <div className="flex flex-wrap -mx-3">
                    <div className="flex items-center w-full max-w-full px-3 shrink-0 md:w-8/12 md:flex-none">
                      <h6 className="mb-0 text-slate-700 dark:text-slate-100">Profile Information</h6>
                    </div>
                    <div className="w-full max-w-full px-3 text-right shrink-0 md:w-4/12 md:flex-none">
                      <button
                        type="button"
                        onClick={openEditModal}
                        className="cursor-pointer bg-transparent border-0 p-0"
                        aria-label="Edit profile"
                      >
                        <i className="leading-normal fas fa-user-edit text-sm text-slate-400 dark:text-slate-500"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex-auto p-4">
                  <p className="leading-normal text-sm text-slate-600 dark:text-slate-300">
                    {(userData as any).bio ||
                      (userData as any).description ||
                      `Welcome, ${displayName}. Manage your profile and preferences here.`}
                  </p>
                  <hr className="h-px my-6 bg-transparent bg-gradient-to-r from-transparent via-slate-200 dark:via-stone-700 to-transparent" />
                  <ul className="flex flex-col pl-0 mb-0 rounded-lg">
                    <li className="relative block px-4 py-2 pt-0 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 rounded-t-lg text-sm text-inherit">
                      <strong className="text-slate-700 dark:text-slate-200">Full Name:</strong> &nbsp;
                      <span className="text-slate-600 dark:text-slate-300">{displayName}</span>
                    </li>
                    <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                      <strong className="text-slate-700 dark:text-slate-200">Email:</strong> &nbsp;
                      <span className="text-slate-600 dark:text-slate-300">{displayEmail}</span>
                    </li>
                    <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                      <strong className="text-slate-700 dark:text-slate-200">Role:</strong> &nbsp;
                      <span className="text-slate-600 dark:text-slate-300">{displayRole}</span>
                    </li>
                    {(userData as any).tenant && (
                      <>
                        <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                          <strong className="text-slate-700 dark:text-slate-200">Firm name:</strong>{' '}
                          &nbsp;
                          <span className="text-slate-600 dark:text-slate-300">
                            {(userData as any).tenant.name ?? '—'}
                          </span>
                        </li>
                        <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                          <strong className="text-slate-700 dark:text-slate-200">Firm slug:</strong>{' '}
                          &nbsp;
                          <span className="text-slate-600 dark:text-slate-300 font-mono text-xs">
                            {(userData as any).tenant.slug ?? '—'}
                          </span>
                        </li>
                        <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                          <strong className="text-slate-700 dark:text-slate-200">Firm status:</strong>{' '}
                          &nbsp;
                          <span className="text-slate-600 dark:text-slate-300">
                            {(userData as any).tenant.is_active === 'Yes' ||
                            (userData as any).tenant.is_active === true
                              ? 'Active'
                              : (userData as any).tenant.is_active === 'No'
                              ? 'Inactive'
                              : String((userData as any).tenant.is_active ?? '—')}
                          </span>
                        </li>
                      </>
                    )}
                    {(userData as any).phone && (
                      <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                        <strong className="text-slate-700 dark:text-slate-200">Mobile:</strong> &nbsp;
                        <span className="text-slate-600 dark:text-slate-300">{(userData as any).phone}</span>
                      </li>
                    )}
                    {(userData as any).location && (
                      <li className="relative block px-4 py-2 pl-0 leading-normal bg-white dark:bg-stone-800 border-0 border-t-0 text-sm text-inherit">
                        <strong className="text-slate-700 dark:text-slate-200">Location:</strong> &nbsp;
                        <span className="text-slate-600 dark:text-slate-300">{(userData as any).location}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            {/* Quick Actions */}
            <div className="w-full max-w-full px-3 lg-max:mt-6 xl:w-4/12">
              <div className="relative flex flex-col h-full min-w-0 break-words bg-white dark:bg-stone-800 border-0 rounded-2xl bg-clip-border">
                <div className="p-4 pb-0 mb-0 bg-white dark:bg-stone-800 border-b-0 rounded-t-2xl">
                  <h6 className="mb-0 text-slate-700 dark:text-slate-100">Quick Actions</h6>
                </div>
                <div className="flex-auto p-4">
                  <div className="space-y-2">
                    <Link
                      to="/dashboard/court-cases/new"
                      className="block w-full px-4 py-2 text-sm text-left text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100       
  dark:hover:bg-slate-700 transition-colors"
                    >
                      Create New Court Case
                    </Link>
                    <Link
                      to="/dashboard/clients/new"
                      className="block w-full px-4 py-2 text-sm text-left text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100       
  dark:hover:bg-slate-700 transition-colors"
                    >
                      Add New Client
                    </Link>
                    <Link
                      to="/dashboard/appointments/new"
                      className="block w-full px-4 py-2 text-sm text-left text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100       
  dark:hover:bg-slate-700 transition-colors"
                    >
                      Schedule Appointment
                    </Link>
                    <Link
                      to="/dashboard/invoices/new"
                      className="block w-full px-4 py-2 text-sm text-left text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100       
  dark:hover:bg-slate-700 transition-colors"
                    >
                      Create Invoice
                    </Link>
                    <Link
                      to="/dashboard/reports"
                      className="block w-full px-4 py-2 text-sm text-left text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100       
  dark:hover:bg-slate-700 transition-colors"
                    >
                      View Reports
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* Recent Court Cases */}
            <div className="flex-none w-full max-w-full px-3 mt-6">
              <div className="relative flex flex-col min-w-0 mb-6 break-words bg-white dark:bg-stone-800 border-0 rounded-2xl bg-clip-border">
                <div className="p-4 pb-0 mb-0 bg-white dark:bg-stone-800 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h6 className="mb-1 text-slate-700 dark:text-slate-100">Recent Court Cases</h6>
                      <p className="leading-normal text-sm text-slate-600 dark:text-slate-300">
                        Your active legal matters
                      </p>
                    </div>
                    <Link
                      to="/dashboard/court-cases"
                      className="text-sm text-primary-600 hover:text-primary-800"
                    >
                      View All
                    </Link>
                  </div>
                </div>
                <div className="flex-auto p-4">
                  {recentCases.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        No court cases yet.
                      </p>
                      <Link
                        to="/dashboard/court-cases/new"
                        className="inline-block px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                      >
                        Create Your First Case
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {recentCases.map((courtCase: any) => (
                        <Link
                          key={courtCase.id}
                          to={`/dashboard/court-cases/${courtCase.id}`}
                          className="block p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-100
  dark:hover:bg-slate-700 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                              {courtCase.case_number ||
                                courtCase.party_name ||
                                `Case #${courtCase.id}`}
                            </h5>
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded ${
                                courtCase.priority === 'High'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  : courtCase.priority === 'Medium'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              }`}
                            >
                              {courtCase.priority || '—'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                            {courtCase.party_name || '—'}
                          </p>
                          {courtCase.filing_date && (
                            <p className="text-xs text-slate-500 dark:text-slate-500">
                              Filed: {String(courtCase.filing_date).slice(0, 10)}
                            </p>
                          )}
                        </Link>
                      ))}
                      <Link
                        to="/dashboard/court-cases/new"
                        className="flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border-2 border-dashed border-slate-300
  dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <div className="text-center">
                          <i className="fa fa-plus text-2xl text-slate-400 dark:text-slate-500 mb-2"></i>
                          <p className="text-sm text-slate-500 dark:text-slate-400">New Case</p>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Edit Profile Modal */}
        {isEditOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-stone-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Edit profile
                </h2>
                <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <i className="fas fa-times text-sm" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await authApi.updateProfile({ name: editName });
                    await refreshUser();
                    toast.success('Profile updated.');
                    setIsEditOpen(false);
                  } catch (err: any) {
                    toast.error(
                      err?.body?.message || err?.message || 'Failed to update profile.',
                    );
                  }
                }}
                className="px-6 py-5 space-y-4"
              >
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    Full name
                  </label>
                  <input
                    className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200 border
  border-slate-200 dark:border-stone-700"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
                    Email
                  </label>
                  <input
                    className="w-full rounded-lg bg-gray-100 py-2 px-3 text-sm text-slate-500 dark:bg-stone-900 dark:text-slate-400 border border-slate-200
  dark:border-stone-700"
                    value={displayEmail}
                    disabled
                  />
                </div>
                <div className="pt-2 border-t border-slate-200 dark:border-stone-700 space-y-3">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Change password
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Current password
                    </label>
                    <input
                      type="password"
                      autoComplete="current-password"
                      className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200 border border-slate-200 dark:border-stone-700"
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Current password"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      New password
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200 border border-slate-200 dark:border-stone-700"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      placeholder="New password (min. 8 characters)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200 border border-slate-200 dark:border-stone-700"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={pwLoading || !currentPw || !newPw || !confirmPw}
                    className="px-3 py-1.5 text-xs rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {pwLoading ? 'Updating...' : 'Update password'}
                  </button>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-stone-700 text-slate-600 dark:text-slate-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };
  export default ProfilePage;